import Link from "next/link";
import type { ReactNode, MouseEventHandler } from "react";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "@/components/icons";

type Variant = "accent" | "dark" | "ghost" | "ghost-dark";
type Size = "md" | "lg" | "sm";

const variantClasses: Record<Variant, string> = {
  accent: "btn-accent",
  dark: "btn-dark",
  ghost: "btn-ghost",
  "ghost-dark": "btn-ghost-on-dark",
};

const sizeClasses: Record<Size, string> = {
  md: "",
  lg: "btn-lg",
  sm: "btn-sm",
};

type CommonProps = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  withArrow?: boolean;
  className?: string;
};

export function ButtonLink({
  href,
  children,
  variant = "accent",
  size = "md",
  withArrow = false,
  className,
  external = false,
  onClick,
  ...rest
}: CommonProps & { href: string; external?: boolean; onClick?: MouseEventHandler } & Record<string, unknown>) {
  const cls = cn("btn", variantClasses[variant], sizeClasses[size], className);
  if (external) {
    return (
      <a href={href} className={cls} target="_blank" rel="noopener noreferrer" onClick={onClick} {...rest}>
        {children}
        {withArrow && <ArrowUpRight />}
      </a>
    );
  }
  return (
    <Link href={href} className={cls} onClick={onClick} {...rest}>
      {children}
      {withArrow && <ArrowUpRight />}
    </Link>
  );
}

export function Button({
  children,
  variant = "accent",
  size = "md",
  withArrow = false,
  className,
  type = "button",
  onClick,
  ...rest
}: CommonProps & { type?: "button" | "submit"; onClick?: MouseEventHandler } & Record<string, unknown>) {
  return (
    <button type={type} className={cn("btn", variantClasses[variant], sizeClasses[size], className)} onClick={onClick} {...rest}>
      {children}
      {withArrow && <ArrowUpRight />}
    </button>
  );
}